import { useState } from "react";
import { Play, ArrowRight, MessageCircle, Rocket } from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { Container } from "../components/ui";
import { MotionProvider, Reveal, PromoBanner, Button as DSButton, PageHeading, SectionHeading } from "../ds/core";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const DOTS = { backgroundImage: "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)", backgroundSize: "16px 16px" } as const;
const SCRIM = "linear-gradient(180deg, rgba(8,11,22,0.3) 0%, rgba(8,11,22,0.25) 40%, rgba(8,11,22,0.78) 100%)";
/* What the play badge's `backdrop-blur` used to do, done with paint instead of a
 * filter. See the badge below for why the filter had to go — the blur read as
 * "frosted glass", but the work it was actually doing was DARKENING the frame
 * behind the disc so a 15%-white circle had something to sit on. A radial fade
 * does that identically, costs nothing, and never moves. */
const HALO = "radial-gradient(closest-side, rgba(8,11,22,0.5), rgba(8,11,22,0))";

/**
 * A CMS media slot (video file, poster) as a plain URL, or `undefined` when the
 * slot is unset. Unlike `cmsImage` there is no bundled fallback to fall back
 * to: a video either exists in this environment or the card stays a poster.
 */
const mediaSlot = (t: ReturnType<typeof useLocale>["t"], key: string): string | undefined =>
  (t(key, { defaultValue: "" }) as string) || undefined;

/* The customer's training videos (DIT-392) — one entry per film he supplied.
 * `id` is both the i18n key suffix and the CMS slot suffix; `group` joins a
 * video to its GROUPS entry.
 *
 * The list follows the films, not the other way round: the three cards that
 * used to sit here (mobileApp, companyDetails, reports) had no film behind
 * them and are gone, `holidaysAbsences` split because holidays and absences
 * arrived as two, and `dashboard` leads because it is the tour a new user
 * should watch first — it is also the featured film at the top of the page. */
const VIDEOS = [
  { id: "dashboard", group: "gettingStarted" },
  { id: "users", group: "gettingStarted" },
  { id: "manageUsers", group: "gettingStarted" },
  { id: "addContact", group: "gettingStarted" },
  { id: "addProject", group: "gettingStarted" },
  { id: "editProject", group: "gettingStarted" },
  { id: "timeRegistrations", group: "dailyUse" },
  { id: "viewRegistration", group: "dailyUse" },
  { id: "overtime", group: "dailyUse" },
  { id: "absences", group: "dailyUse" },
  { id: "holidays", group: "dailyUse" },
  { id: "extraWork", group: "dailyUse" },
  { id: "transport", group: "dailyUse" },
  { id: "trips", group: "dailyUse" },
  { id: "subcontractors", group: "forTheOffice" },
  { id: "subcontractorsInProjects", group: "forTheOffice" },
  { id: "exportUsers", group: "forTheOffice" },
] as const;

/** The film the page leads with — also the first card of the library. */
const FEATURED_ID = "dashboard";

const GROUPS = [
  { key: "gettingStarted" },
  { key: "dailyUse" },
  { key: "forTheOffice" },
] as const;

/**
 * A library card. Until it is clicked it is only a poster, so a page of 18
 * cards costs 18 poster images rather than 18 video streams; the `<video>`
 * element is mounted on click, which is also what starts the download.
 *
 * `src` is the customer's training video (DIT-392). It comes from a CMS slot
 * and is env-specific, so a card whose slot is empty simply never becomes
 * playable and keeps the poster treatment — the page degrades to what it was
 * before the videos existed instead of rendering a broken player.
 */
export function VideoCard({
  title, desc, src, poster, posterAlt,
}: { title: string; desc: string; src?: string; poster?: string; posterAlt?: string }) {
  const [playing, setPlaying] = useState(false);
  const playable = Boolean(src);

  return (
    <div className={`group flex h-full w-full flex-col overflow-hidden rounded-3xl border-8 border-white bg-bg-subtle shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-shadow duration-300 hover:shadow-[0_20px_50px_-26px_rgba(15,23,42,0.35)] ${playable ? "" : "cursor-default"}`}>
      <div className="relative aspect-video shrink-0 overflow-hidden rounded-2xl bg-ds-hero">
        {playing ? (
          // autoPlay because the click that mounted this WAS the play intent.
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 h-full w-full bg-black object-contain"
          />
        ) : (
          <button
            type="button"
            onClick={() => playable && setPlaying(true)}
            aria-label={playable ? `${title} — play` : title}
            disabled={!playable}
            className="absolute inset-0 h-full w-full cursor-pointer disabled:cursor-default"
          >
            {poster ? (
              <img src={poster} alt={posterAlt ?? ""} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <span aria-hidden className="absolute inset-0 opacity-60" style={DOTS} />
            )}
            {/* No `backdrop-blur` on this badge, deliberately (DIT-553). A backdrop
                filter makes Chrome re-resolve and re-blur what is behind the element
                whenever anything about it moves — and everything about this one moves:
                it scales for 300ms on hover, the card fades and translates in as it
                scrolls into view, and on the featured player a poster scales behind it
                while an `animate-ping` runs inside it forever. Chrome dropped the
                card's content for a frame while it recomputed, which is the flicker
                the customer reported. Removing only the hover scale would not have
                been enough: the reveal above puts `opacity < 1` on an ancestor, which
                makes that ancestor the backdrop root, so what this element blurs
                changes for every frame of the scroll-in as well.

                The disc itself is untouched — the design is the same glass circle,
                ring and white mark. What replaces the filter is the `HALO` behind it:
                the blur's real contribution was darkening the frame under the disc,
                not blurring it, and a static radial fade does that for free. */}
            {/* The play mark only where there is something to play. A card whose CMS
                slot is empty keeps the poster and nothing else: the button is already
                disabled, but a visible play button that ignores the click reads as a
                broken page rather than as a film that is not there (DIT-553, QA). */}
            {playable && (
              <>
                <span aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: HALO }} />
                <span className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/15 ring-1 ring-white/50 transition-transform duration-300 group-hover:scale-110">
                  <Play size={24} className="ml-0.5 text-white" fill="currentColor" />
                </span>
              </>
            )}
          </button>
        )}
      </div>
      <div className="flex flex-1 flex-col px-3 pb-5 pt-4">
        <h3 className="font-display text-ds-18 font-semibold leading-snug text-ds-ink">{title}</h3>
        <p className="mt-1.5 text-ds-14 leading-relaxed text-ds-ink-soft">{desc}</p>
      </div>
    </div>
  );
}

/** The hero video — same play-on-click contract as the library cards. */
function FeaturedVideo() {
  const { t } = useLocale();
  const [playing, setPlaying] = useState(false);
  const src = mediaSlot(t, `trainings:video.${FEATURED_ID}.src`);
  const title = t(`trainings:video.${FEATURED_ID}.title`);

  return (
    <section className="bg-bg font-ds">
      <div className="px-4 py-3">
        <div className="group relative block aspect-[16/10] overflow-hidden rounded-[32px] bg-ds-hero md:aspect-[16/7]">
          {playing ? (
            <video src={src} controls autoPlay playsInline className="absolute inset-0 h-full w-full bg-black object-contain" />
          ) : (
            <>
              <button
                type="button"
                onClick={() => src && setPlaying(true)}
                aria-label={src ? `${title} — play` : title}
                disabled={!src}
                className="absolute inset-0 h-full w-full cursor-pointer disabled:cursor-default"
              >
                <img src={cmsImage(t, "trainings:featured.image", `/trainings/${FEATURED_ID}.jpg`)} alt={t("trainings:featured.posterAlt")} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <span aria-hidden className="absolute inset-0" style={{ background: SCRIM }} />
                {/* No `backdrop-blur` here either — see the library card above (DIT-553).
                    The halo stays outside the badge so it never scales with it. */}
                <span aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: HALO }} />
                <span className="absolute left-1/2 top-1/2 grid h-24 w-24 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/15 ring-1 ring-white/50 transition-transform duration-300 group-hover:scale-110">
                  <span aria-hidden className="absolute inset-0 animate-ping rounded-full ring-1 ring-white/40" />
                  <Play size={30} className="relative ml-1 text-white" fill="currentColor" />
                </span>
              </button>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 p-8 md:p-12">
                <SectionHeading tone="dark" eyebrow={t("trainings:featured.eyebrow")} title={title} />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Trainings() {
  const { t } = useLocale();
  return (
    <>
      {/* 1 ── Blue hero */}
      <PageHeading
        eyebrow={t("trainings:hero.eyebrow")}
        title={t("trainings:hero.title")}
        intro={t("trainings:hero.intro")}
        actions={<>
          <DSButton to={TRIAL} variant="solid-light">{t("trainings:hero.startTrial")}</DSButton>
          <DSButton to="/getting-started" variant="ghost">{t("trainings:hero.gettingStarted")}</DSButton>
        </>}
      />

      {/* 2 ── Featured video */}
      <FeaturedVideo />

      {/* 3 ── Video library — grouped, DS cards */}
      <MotionProvider>
        <section className="bg-bg font-ds">
          <Container className="py-16 md:py-20">
            <div className="flex flex-col gap-14">
              {GROUPS.map((g) => (
                <div key={g.key}>
                  <SectionHeading eyebrow={t(`trainings:group.${g.key}.eyebrow`)} title={t(`trainings:group.${g.key}.title`)} />
                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    {VIDEOS.filter((v) => v.group === g.key).map((v, i) => (
                      <Reveal key={v.id} delay={i * 0.05} className="flex">
                        <VideoCard
                          title={t(`trainings:video.${v.id}.title`)}
                          desc={t(`trainings:video.${v.id}.desc`)}
                          src={mediaSlot(t, `trainings:video.${v.id}.src`)}
                          poster={cmsImage(t, `trainings:video.${v.id}.poster`, `/trainings/${v.id}.jpg`)}
                          posterAlt={t(`trainings:video.${v.id}.title`)}
                        />
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>
      </MotionProvider>

      {/* 4 ── Cross-links */}
      <section className="bg-bg-subtle font-ds">
        <Container className="py-16">
          <div className="grid gap-3 md:grid-cols-2">
            <LocaleLink to="/getting-started" className="group flex flex-col rounded-3xl border border-line bg-surface p-6 md:p-8 transition-colors hover:bg-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ds-tint text-ds-accent transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                <Rocket size={20} strokeWidth={2} />
              </span>
              <h3 className="mt-4 font-display text-ds-22 font-semibold text-ds-ink">{t("trainings:crossLinks.gettingStarted.title")}</h3>
              <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ds-ink-soft">{t("trainings:crossLinks.gettingStarted.body")}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary">
                {t("trainings:crossLinks.gettingStarted.cta")} <ArrowRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </LocaleLink>
            <LocaleLink to="/contact" className="group flex flex-col rounded-3xl border border-line bg-surface p-6 md:p-8 transition-colors hover:bg-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ds-tint text-ds-accent transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                <MessageCircle size={20} strokeWidth={2} />
              </span>
              <h3 className="mt-4 font-display text-ds-22 font-semibold text-ds-ink">{t("trainings:crossLinks.contact.title")}</h3>
              <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ds-ink-soft">{t("trainings:crossLinks.contact.body")}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary">
                {t("trainings:crossLinks.contact.cta")} <ArrowRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </LocaleLink>
          </div>
        </Container>
      </section>

      {/* 5 ── Closing CTA */}
      <PromoBanner
        eyebrow={t("trainings:promo.eyebrow")}
        title={t("trainings:promo.title")}
        body={t("trainings:promo.body")}
        action={<DSButton to={TRIAL} variant="solid-light">{t("trainings:promo.startTrial")}</DSButton>}
      />
    </>
  );
}

